import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import { deepPurple, grey } from "@mui/material/colors";

import { Button, Typography } from "@analyticsMD/quil";

import { mapDenialPreventionCriteriaToRows } from "./aras-score-denial-prevention-map";
import { ARAS_SCORE_MOCK_CATEGORIES } from "./aras-score-mock-categories";
import {
  ARAS_BODY_PADDING_X,
  ARAS_CHEVRON_COL_WIDTH,
  ARAS_CHEVRON_DOT_GAP,
  ARAS_DIVIDER_LINE,
  ARAS_EXPANDED_PANEL_BORDER,
  ARAS_EXPANDED_PANEL_MARGIN_LEFT,
  ARAS_FONT_EXPANDED,
  ARAS_FONT_PRIMARY,
  arasExpandedPanelMuiTypographySx,
  arasFooterMuiTypographySx,
  arasFullBleedDividerSx,
  arasPopoverMuiTypographyPrimarySx,
  BAND_STYLES,
  footerSx,
  rowDividerSx,
} from "./aras-score-popover-tokens";
import {
  ArasScoreCellTooltip,
  resolveRowScoreTooltip,
} from "./aras-score-popover-tooltip";
import type {
  ArasScoreCategoryRow,
  ArasScorePopoverContentProps,
} from "./aras-score-types";
import { ARAS_POPOVER_PAPER_MAX_WIDTH } from "./suggestion-detail-popover-shell";
import Info from "@mui/icons-material/Info";

/** First paint: prefer `defaultExpandedId` when it matches a row, else mock default id, else first row. */
function initialExpandedCategoryId(
  rows: ArasScoreCategoryRow[],
  defaultExpandedId: string | null | undefined
): string | null {
  if (
    defaultExpandedId != null &&
    rows.some((row) => row.id === defaultExpandedId)
  ) {
    return defaultExpandedId;
  }
  if (rows.some((row) => row.id === "medical-complexity")) {
    return "medical-complexity";
  }
  return rows[0]?.id ?? null;
}

function initialExpandedIds(
  rows: ArasScoreCategoryRow[],
  defaultExpandedId: string | null | undefined
): Set<string> {
  const id = initialExpandedCategoryId(rows, defaultExpandedId);
  return id != null ? new Set([id]) : new Set();
}

const ArasScorePopoverView: FC<ArasScorePopoverContentProps> = ({
  onClose,
  denialPrevention,
  totalScore: totalScoreProp,
  categories: categoriesProp,
  defaultExpandedId = "medical-complexity",
}) => {
  const categories = useMemo(() => {
    if (denialPrevention != null) {
      return mapDenialPreventionCriteriaToRows(denialPrevention.criteria ?? []);
    }
    return categoriesProp ?? ARAS_SCORE_MOCK_CATEGORIES;
  }, [denialPrevention, categoriesProp]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    initialExpandedIds(categories, defaultExpandedId)
  );

  const categoryRowIdsKey = useMemo(
    () => categories.map((c) => c.id).join("\0"),
    [categories]
  );

  useEffect(() => {
    setExpandedIds((prev) => {
      const kept = new Set<string>();
      Array.from(prev).forEach((id) => {
        if (categories.some((row) => row.id === id)) {
          kept.add(id);
        }
      });
      if (kept.size > 0) {
        return kept;
      }
      return initialExpandedIds(categories, defaultExpandedId);
    });
  }, [categoryRowIdsKey, categories, defaultExpandedId]);

  const computedTotal = useMemo(
    () => categories.reduce((sum, row) => sum + row.points, 0),
    [categories]
  );
  const totalScore =
    totalScoreProp ??
    (denialPrevention != null ? denialPrevention.total_score : undefined) ??
    computedTotal;

  const toggleRow = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Box
      className="aras-score-popover"
      sx={{
        padding: ARAS_BODY_PADDING_X,
        maxWidth: ARAS_POPOVER_PAPER_MAX_WIDTH,
        fontSize: `${ARAS_FONT_PRIMARY} !important`,
        lineHeight: 1.45,
        "& .MuiButton-root": {
          fontSize: `${ARAS_FONT_PRIMARY} !important`,
        },
        ...arasPopoverMuiTypographyPrimarySx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          paddingBottom: "0.75rem",
          marginLeft: `calc(-1 * ${ARAS_BODY_PADDING_X})`,
          marginRight: `calc(-1 * ${ARAS_BODY_PADDING_X})`,
          paddingLeft: ARAS_BODY_PADDING_X,
          paddingRight: ARAS_BODY_PADDING_X,
          borderBottom: ARAS_DIVIDER_LINE,
        }}
      >
        <Button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          size="small"
          sx={{
            textTransform: "none",
            minWidth: "auto",
            margin: 0,
            padding: "0 !important",
            fontSize: `${ARAS_FONT_PRIMARY} !important`,
            fontWeight: 600,
            "&.MuiButtonBase-root": {
              paddingLeft: "0 !important",
              paddingRight: "0 !important",
            },
          }}
          color="primary"
        >
          Hide
        </Button>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "0.5rem",
            flexShrink: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: `${ARAS_FONT_PRIMARY} !important`,
              color: grey[900],
            }}
          >
            Total ARAS Score
          </Box>
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: `${ARAS_FONT_PRIMARY} !important`,
              color: grey[900],
              lineHeight: 1,
            }}
          >
            {totalScore}
          </Box>
        </Box>
      </Box>

      <Box
        component="ul"
        sx={{
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {denialPrevention?.score_rationale && (
          <Box
            component="li"
            sx={{
              display: "block",
              listStyle: "none",
              margin: 0,
              padding: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: "0.25rem",
                padding: "0.65rem 0",
              }}
            >
              <Box
                sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
              >
                <Info sx={{ color: deepPurple[800] }} />
                <Typography
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: "600",
                    color: grey[600],
                  }}
                >
                  ARAS Summary
                </Typography>
              </Box>
              <Typography sx={{ paddingLeft: "2rem" }}>
                {denialPrevention.score_rationale}
              </Typography>
            </Box>
            <Box component="div" aria-hidden sx={rowDividerSx} />
          </Box>
        )}
        {categories.map((row, index) => {
          const expanded = expandedIds.has(row.id);
          const colors = BAND_STYLES[row.band];
          const showDivider = index > 0;

          return (
            <Box
              key={row.id}
              component="li"
              sx={{
                display: "block",
                listStyle: "none",
                margin: 0,
                padding: 0,
              }}
            >
              {showDivider ? (
                <Box component="div" aria-hidden sx={rowDividerSx} />
              ) : null}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: "0.25rem",
                  padding: "0.65rem 0",
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => toggleRow(row.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    minWidth: 0,
                    gap: ARAS_CHEVRON_DOT_GAP,
                    padding: 0,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: `${ARAS_FONT_PRIMARY} !important`,
                    lineHeight: 1.45,
                    fontWeight: 400,
                    textAlign: "left",
                    color: grey[900],
                    "&:focus-visible": {
                      outline: `2px solid ${grey[400]}`,
                      outlineOffset: "2px",
                      borderRadius: "0.25rem",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      flexShrink: 0,
                      width: ARAS_CHEVRON_COL_WIDTH,
                      minWidth: ARAS_CHEVRON_COL_WIDTH,
                      color: grey[500],
                      margin: 0,
                      padding: 0,
                    }}
                    aria-hidden
                  >
                    {expanded ? (
                      <ExpandLess
                        sx={{
                          display: "block",
                          fontSize: `${ARAS_FONT_PRIMARY} !important`,
                        }}
                      />
                    ) : (
                      <ExpandMore
                        sx={{
                          display: "block",
                          fontSize: `${ARAS_FONT_PRIMARY} !important`,
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: "0.5rem",
                      height: "0.5rem",
                      borderRadius: "50%",
                      backgroundColor: colors.dot,
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      fontSize: `${ARAS_FONT_PRIMARY} !important`,
                      fontWeight: 400,
                    }}
                  >
                    {row.label}
                  </Box>
                </Box>
                <ArasScoreCellTooltip
                  score={row.points}
                  color={colors.score}
                  {...(row.scoreLegendTooltip === false
                    ? { disableTooltip: true as const }
                    : { title: resolveRowScoreTooltip(row) })}
                  ariaLabel={`Score for ${row.label}`}
                />
              </Box>
              {expanded && row.expandedDetail ? (
                <Box
                  sx={{
                    marginBottom: "10px",
                    marginLeft: ARAS_EXPANDED_PANEL_MARGIN_LEFT,
                    marginRight: 0,
                    padding: "0.5rem",
                    border: ARAS_EXPANDED_PANEL_BORDER,
                    borderRadius: "0.375rem",
                    backgroundColor: grey[50],
                    ...arasExpandedPanelMuiTypographySx,
                  }}
                >
                  <Box
                    component="p"
                    sx={{
                      margin: 0,
                      fontStyle: "italic",
                      fontSize: `${ARAS_FONT_EXPANDED} !important`,
                      color: grey[700],
                      lineHeight: 1.5,
                    }}
                  >
                    {row.expandedDetail}
                  </Box>
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          ...arasFullBleedDividerSx,
          marginTop: "0.75rem",
          paddingTop: "0.5rem",
          paddingLeft: ARAS_BODY_PADDING_X,
          paddingRight: ARAS_BODY_PADDING_X,
        }}
      >
        <Box sx={{ ...footerSx, ...arasFooterMuiTypographySx }}>
          ARAS predictions are most accurate with complete clinical
          documentation. Patients with scores ≥13 generally have higher payor
          approval for acute rehabilitation.
        </Box>
      </Box>
    </Box>
  );
};

export default ArasScorePopoverView;
